<template>
    <div class="relative">
        <button
            @click.prevent="toggleDropdown"
            @keydown.enter="toggleDropdown"
            @keydown.space.prevent="toggleDropdown"
            :disabled="disabled"
            type="button"
            tabindex="0"
            class="font-medium whitespace-nowrap rounded shadow border inline-flex items-center gap-x-1.5 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 hover:border-slate-800 dark:hover:border-slate-500 focus:outline focus:border-slate-800 dark:focus:border-slate-500 focus:outline-none disabled:opacity-50 disabled:text-slate-800 dark:disabled:text-slate-300 disabled:bg-white dark:disabled:bg-slate-800 disabled:border-slate-300 dark:disabled:border-slate-600"
        >
            <div>{{ label }}</div>
            <div
                v-if="showCounter"
                class="px-1.5 py-0.5 bg-slate-800 dark:bg-slate-600 rounded-[999px] flex-col justify-center items-center gap-2.5 flex text-white text-xs font-medium leading-none w-4"
                :class="{
                    'xl:invisible max-xl:hidden': selectedItems.length === 0,
                }"
            >
                {{ selectedItems.length }}
            </div>
            <Icon icon="chevron-down" class="w-4 h-4" />
        </button>
        <!-- START DROPDOWN -->
        <div
            v-if="isOpen"
            class="min-w-full p-2"
            :class="dropdownClass"
            v-click-outside="() => (isOpen = false)"
        >
            <ul class="max-h-60 overflow-auto">
                <!-- slot for custom options -->
                <slot>
                    <!-- fallback content: -->
                    <li
                        v-for="(option, index) in options"
                        :key="index"
                        :class="optionClass + ' cursor-pointer'"
                    >
                        <input
                            type="checkbox"
                            class="py-0.5 justify-start items-center flex text-orange-600 w-4 h-4 bg-white dark:bg-slate-700 rounded border border-slate-300 dark:border-slate-600 focus:ring-0 focus:ring-offset-0 focus:outline-none cursor-pointer"
                            :id="option[valueKey]"
                            :name="option[labelKey]"
                            :checked="isSelected(option)"
                            @change="toggleSelect(option)"
                        />
                        <label :for="option[valueKey]" :class="labelClass">
                            <p>{{ option[labelKey] }}</p>
                        </label>
                    </li>
                </slot>
            </ul>
        </div>
    </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import vClickOutside from 'click-outside-vue3';
import Icon from '@/components/ui/icon/icon.vue';

export default {
    name: 'MultiSelect',
    components: {
        Icon,
    },
    props: {
        options: {
            type: Array,
            required: false,
        },
        modelValue: {
            type: Array,
            default: [],
        },
        labelKey: {
            type: String,
            default: 'label',
        },
        valueKey: {
            type: String,
            default: 'value',
        },
        label: {
            type: String,
            default: 'Select options...',
        },
        buttonClass: {
            type: String,
            default:
                'gap-2 pl-4 pr-3 py-2 bg-white dark:bg-slate-800 rounded shadow border border-slate-200 dark:border-slate-700 justify-center items-center flex text-slate-800 dark:text-slate-100 text-sm font-medium whitespace-nowrap min-w-content',
        },
        dropdownClass: {
            type: String,
            default:
                'absolute w-fit mt-1 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 shadow-sm z-10',
        },
        optionClass: {
            type: String,
            default:
                'p-2 rounded justify-start items-center flex gap-2 text-slate-800 dark:text-slate-100 text-sm font-normal leading-tight whitespace-nowrap cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700',
        },
        labelClass: {
            type: String,
            default: 'hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer',
        },
        showCounter: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue', 'changed'],
    setup(props, { emit }) {
        const isOpen = ref(false);
        const selectedItems = ref([...props.modelValue]);

        const toggleDropdown = () => {
            isOpen.value = !isOpen.value;
        };

        const isSelected = (option) => {
            return selectedItems.value.some(
                (item) => item[props.valueKey] === option[props.valueKey]
            );
        };

        const toggleSelect = (option) => {
            if (isSelected(option)) {
                selectedItems.value = selectedItems.value.filter(
                    (item) => item[props.valueKey] !== option[props.valueKey]
                );
            } else {
                selectedItems.value.push(option);
            }

            emit('update:modelValue', selectedItems.value);
            emit('changed');
        };

        watch(
            () => props.modelValue,
            (newVal) => {
                selectedItems.value = [...newVal];
            }
        );

        return {
            isOpen,
            isSelected,
            selectedItems,
            toggleDropdown,
            toggleSelect,
        };
    },
};
</script>
